import { useEffect, useState } from "react";
import { Plus, Loader2, Truck, Trash2, ListChecks, Mail, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BUREAU_COLORS, STATUS_COLORS } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const BUREAUS = ["Equifax", "Experian", "TransUnion", "Furnisher"];
const STATUSES = ["Drafted", "Dispatched Certified", "Received by Bureau", "Under 4-Day Review", "Additional Docs Requested", "Blocked & Deleted", "Escalated to CFPB"];
const TYPES = ["FCRA § 605B Identity Theft Block", "FCRA § 623 Direct Furnisher Dispute", "FCRA § 611 30-Day Reinvestigation", "Unauthorized Inquiry Removal", "4-Day Statutory Demand"];

export default function DisputeTracker() {
  const { apiErr } = useAuth();
  const [items, setItems] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bureau, setBureau] = useState("all");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ consumerId: "", bureau: "Equifax", creditor: "", accountNumber: "", disputeType: TYPES[0], amount: "", trackingNumber: "", urgency: "High", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/disputes", { params: { bureau, status } });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bureau, status]);
  useEffect(() => { api.get("/consumers").then(({ data }) => setConsumers(data)); }, []);

  const create = async () => {
    if (!form.consumerId) { toast.error("Select a consumer"); return; }
    setSaving(true);
    try {
      const c = consumers.find((x) => x.id === form.consumerId);
      await api.post("/disputes", { ...form, consumerName: c?.fullName || "" });
      toast.success("Dispute added to pipeline");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (id, newStatus) => {
    await api.put(`/disputes/${id}`, { status: newStatus });
    toast.success("Status updated"); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this dispute?")) return;
    await api.delete(`/disputes/${id}`); toast.success("Deleted"); load();
  };

  const emailLetter = async (id) => {
    try {
      const { data } = await api.post(`/disputes/${id}/email`, {});
      toast.success(`Letter emailed to ${data.to}`); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const track = async (id) => {
    try {
      const { data } = await api.post(`/disputes/${id}/track`, {});
      if (data.configured === false) toast.info(data.message);
      else toast.success(`USPS status: ${data.status || "unknown"}${data.events?.length ? ` · ${data.events.length} events` : ""}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="tracking-panel">
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex gap-3">
          <Select value={bureau} onValueChange={setBureau}>
            <SelectTrigger className="w-40 bg-[#FFFFFF] border-neutral-200" data-testid="dispute-bureau-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All bureaus</SelectItem>{BUREAUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-52 bg-[#FFFFFF] border-neutral-200" data-testid="dispute-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
        <Button onClick={() => { setForm({ ...form, disputeType: "FCRA § 611 30-Day Reinvestigation" }); setOpen(true); }} variant="outline" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="thirty-day-dispute-button">
          <Plus className="h-4 w-4 mr-1.5" /> 30-Day Dispute
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="create-new-dispute-button">
              <Plus className="h-4 w-4 mr-1.5" /> New Dispute
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F6EEEE] border-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">New Dispute</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label className="text-neutral-600">Consumer</Label>
                <Select value={form.consumerId} onValueChange={(v) => setForm({ ...form, consumerId: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid="dispute-consumer-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{consumers.map((c) => <SelectItem key={c.id} value={c.id}>{c.fullName}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-neutral-600">Bureau</Label>
                <Select value={form.bureau} onValueChange={(v) => setForm({ ...form, bureau: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#FFFFFF] border-neutral-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{BUREAUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-neutral-600">Type</Label>
                <Select value={form.disputeType} onValueChange={(v) => setForm({ ...form, disputeType: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#FFFFFF] border-neutral-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <TField label="Creditor" v={form.creditor} onC={(v) => setForm({ ...form, creditor: v })} />
              <TField label="Account #" v={form.accountNumber} onC={(v) => setForm({ ...form, accountNumber: v })} />
              <TField label="Amount" v={form.amount} onC={(v) => setForm({ ...form, amount: v })} placeholder="$12,400" />
              <TField label="Tracking #" v={form.trackingNumber} onC={(v) => setForm({ ...form, trackingNumber: v })} />
              <div className="col-span-2">
                <Label className="text-neutral-600">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="save-dispute-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Dispute"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#C1121F]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-neutral-400"><ListChecks className="h-10 w-10 mx-auto opacity-40 mb-3" />No disputes match these filters.</div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => {
            const bc = BUREAU_COLORS[d.bureau] || BUREAU_COLORS.Furnisher;
            const days = d.statutoryDeadline ? Math.ceil((new Date(d.statutoryDeadline) - new Date()) / 86400000) : null;
            const resolved = ["Blocked & Deleted", "Block Confirmed", "Resolved / Blocked"].includes(d.status);
            return (
              <div key={d.id} className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-5" data-testid="dispute-row">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    <span className={`h-2.5 w-2.5 rounded-full ${bc.dot} shrink-0`} />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{d.consumerName} · <span className="text-neutral-500 font-normal">{d.creditor || d.disputeType}</span></div>
                      <div className="text-xs text-neutral-400 flex flex-wrap items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-md border ${bc.chip} ${bc.text} ${bc.border}`}>{d.bureau}</span>
                        <span className="font-mono">{d.accountNumber}</span>
                        {d.trackingNumber && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{d.trackingNumber}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[#9B0E18]">{d.amount}</span>
                    {!resolved && days !== null && (
                      <span className={`text-xs font-mono px-2 py-1 rounded-md border ${days < 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d SLA`}
                      </span>
                    )}
                    <Select value={d.status} onValueChange={(v) => changeStatus(d.id, v)}>
                      <SelectTrigger className={`w-56 h-9 border text-xs ${STATUS_COLORS[d.status] || "bg-stone-100 text-stone-600 border-stone-300"}`} data-testid="dispute-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => emailLetter(d.id)} className="text-neutral-400 hover:text-[#9B0E18] transition-colors" title="Email letter to consumer" data-testid="email-dispute-button"><Mail className="h-4 w-4" /></button>
                    <button onClick={() => track(d.id)} className="text-neutral-400 hover:text-cyan-300 transition-colors" title="Refresh USPS tracking" data-testid="track-dispute-button"><RefreshCw className="h-4 w-4" /></button>
                    <button onClick={() => remove(d.id)} className="text-neutral-400 hover:text-rose-400 transition-colors" data-testid="delete-dispute-button"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TField({ label, v, onC, placeholder }) {
  return (
    <div>
      <Label className="text-neutral-600">{label}</Label>
      <Input value={v} onChange={(e) => onC(e.target.value)} placeholder={placeholder} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" />
    </div>
  );
}
